/**
 * <PasswordStrengthMeter password={value} />
 *
 * zxcvbn-powered visual strength indicator. Renders a 4-segment bar
 * coloured by zxcvbn's 0-4 score plus a short verdict line. Lazy-imports
 * zxcvbn so the ~400KB dictionary only loads on pages that actually
 * mount this component.
 *
 * NOTE: zxcvbn is for *user feedback only* — server-side rules (min 10
 * chars + HIBP breach check) are the actual enforcement.
 */
import React, { useEffect, useMemo, useState } from 'react';

const LABELS = ['Too weak', 'Weak', 'Okay', 'Strong', 'Excellent'];
const COLOURS = ['#DC2626', '#F97316', '#F59E0B', '#16A34A', '#0F766E'];

const PasswordStrengthMeter = ({ password = '', minLength = 10 }) => {
  const [zxcvbn, setZxcvbn] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!password) return undefined;
    if (zxcvbn) return undefined;
    import('zxcvbn').then((mod) => {
      if (!cancelled) setZxcvbn(() => mod.default || mod);
    }).catch((err) => {
      console.error('Failed to load zxcvbn', err);
    });
    return () => { cancelled = true; };
  }, [password, zxcvbn]);

  const evaluation = useMemo(() => {
    if (!password) return { score: -1, feedback: '' };
    const tooShort = password.length < minLength;
    if (!zxcvbn) {
      return {
        score: tooShort ? 0 : 1,
        feedback: tooShort ? `Needs at least ${minLength} characters` : 'Checking strength…',
      };
    }
    const result = zxcvbn(password);
    const score = result.score;  // 0-4
    let feedback = LABELS[score];
    const warning = result.feedback?.warning;
    if (tooShort) {
      feedback = `Needs at least ${minLength} characters`;
    } else if (warning) {
      feedback = `${LABELS[score]} — ${warning}`;
    }
    return { score: tooShort ? 0 : score, feedback };
  }, [password, zxcvbn, minLength]);

  if (!password) return null;

  const score = Math.max(0, evaluation.score);
  return (
    <div className="mt-2" data-testid="password-strength-meter">
      <div className="flex gap-1.5 h-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex-1 rounded-full transition-colors"
            style={{
              backgroundColor: i <= score ? COLOURS[score] : '#E2E8F0',
            }}
          />
        ))}
      </div>
      <p className="text-xs mt-1.5" style={{ color: COLOURS[score] }} data-testid="password-strength-feedback">
        {evaluation.feedback}
      </p>
    </div>
  );
};

export default PasswordStrengthMeter;
